import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, LOCALE_ID } from '@angular/core';

import { MessageService } from 'primeng/api';
import { acceptLanguageInterceptor, apiErrorInterceptor } from '@adlatus-gui/shared';
import { BASE_PATH as CLEARING_API_URI } from '@adlatus-gui/domain/clearing';
import { authInterceptor, provideAuthentication } from '@adlatus-gui/domain/authentication';
import {
  BASE_PATH as CLEARING_CUSTOM_API_URI,
  ClearingTicketCustomService,
  ITU_CODE,
  LIGHT_PARTNER_API_PATH,
  OWNER_SUBENTITIES
} from '@adlatus-gui/domain/clearing-custom';
import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';
import { MonacoEditorModule, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { SEARCH_ENDPOINT } from '../../../../libs/domain/src/lib/domain/search';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');

export function onMonacoLoad() {
  // Register the lucene language
  // @ts-expect-error - Monaco typings are not up to date
  monaco.languages.register({ id: 'lucene' });

  // Set up the tokenizer for syntax highlighting
  // @ts-expect-error - Monaco typings are not up to date
  monaco.languages.setMonarchTokensProvider('lucene', {
    tokenizer: {
      root: [
        // Keywords
        [/\b(AND|OR|NOT|TO)\b/, 'keyword'],

        // ClearingTicketEntity properties
        [/\b(id|creationDate|lastUpdate|description|originator|processor|externalId|severity|severityChangeReason|requestedResolutionDate|ticketType|resolutionDate|resolveAttachment|resolvedSuccessfully|status|statusChange|note|clearingDataJson|clearingData|troubleTicketRelationship|href|atType|atBaseType|processorGeneratedId|syncChanges|lastSyncErrorChange|deleted)\b/, 'identifier'],

        // Nested entity fields
        [/\b(attachments|noteItem|statusChangeItem|syncChange)\b/, 'identifier'],

        // String literals
        [/"[^"]*"/, 'string'],

        // Numbers
        [/\d+(\.\d+)?/, 'number'],

        // Date patterns
        [/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}/, 'date'],
        [/\d{4}-\d{2}-\d{2}/, 'date'],

        // Brackets
        [/[{}[\]()]/, '@brackets'],

        // Delimiters
        [/:/, 'delimiter'],

        // Wildcards
        [/[*?]/, 'wildcard'],

        // Operators
        [/\^/, 'operator'],
        [/[+-]/, 'operator'],
        [/~\d*(\.\d+)?/, 'operator'],
      ]
    }
  });

  // Register completion item provider
  // @ts-expect-error - Monaco typings are not up to date
  monaco.languages.registerCompletionItemProvider('lucene', {
    provideCompletionItems: () => {
      const suggestions = [
        // Basic fields
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'id', kind: monaco.languages.CompletionItemKind.Field, insertText: 'id:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'creationDate', kind: monaco.languages.CompletionItemKind.Field, insertText: 'creationDate:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'lastUpdate', kind: monaco.languages.CompletionItemKind.Field, insertText: 'lastUpdate:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'description', kind: monaco.languages.CompletionItemKind.Field, insertText: 'description:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'originator', kind: monaco.languages.CompletionItemKind.Field, insertText: 'originator:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'processor', kind: monaco.languages.CompletionItemKind.Field, insertText: 'processor:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'externalId', kind: monaco.languages.CompletionItemKind.Field, insertText: 'externalId:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'severity', kind: monaco.languages.CompletionItemKind.Field, insertText: 'severity:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'severityChangeReason', kind: monaco.languages.CompletionItemKind.Field, insertText: 'severityChangeReason:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'requestedResolutionDate', kind: monaco.languages.CompletionItemKind.Field, insertText: 'requestedResolutionDate:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'ticketType', kind: monaco.languages.CompletionItemKind.Field, insertText: 'ticketType:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'resolutionDate', kind: monaco.languages.CompletionItemKind.Field, insertText: 'resolutionDate:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'resolvedSuccessfully', kind: monaco.languages.CompletionItemKind.Field, insertText: 'resolvedSuccessfully:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'clearingDataJson', kind: monaco.languages.CompletionItemKind.Field, insertText: 'clearingDataJson:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'clearingData', kind: monaco.languages.CompletionItemKind.Field, insertText: 'clearingData:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'href', kind: monaco.languages.CompletionItemKind.Field, insertText: 'href:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'atType', kind: monaco.languages.CompletionItemKind.Field, insertText: 'atType:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'atBaseType', kind: monaco.languages.CompletionItemKind.Field, insertText: 'atBaseType:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'processorGeneratedId', kind: monaco.languages.CompletionItemKind.Field, insertText: 'processorGeneratedId:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'deleted', kind: monaco.languages.CompletionItemKind.Field, insertText: 'deleted:' },

        // Nested fields
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'resolveAttachment', kind: monaco.languages.CompletionItemKind.Field, insertText: 'resolveAttachment:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'status', kind: monaco.languages.CompletionItemKind.Field, insertText: 'status:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'statusChange', kind: monaco.languages.CompletionItemKind.Field, insertText: 'statusChange:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'note', kind: monaco.languages.CompletionItemKind.Field, insertText: 'note:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'troubleTicketRelationship', kind: monaco.languages.CompletionItemKind.Field, insertText: 'troubleTicketRelationship:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'syncChanges', kind: monaco.languages.CompletionItemKind.Field, insertText: 'syncChanges:' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'lastSyncErrorChange', kind: monaco.languages.CompletionItemKind.Field, insertText: 'lastSyncErrorChange:' },

        // Logical operators
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'AND', kind: monaco.languages.CompletionItemKind.Keyword, insertText: 'AND ' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'OR', kind: monaco.languages.CompletionItemKind.Keyword, insertText: 'OR ' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'NOT', kind: monaco.languages.CompletionItemKind.Keyword, insertText: 'NOT ' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: 'TO', kind: monaco.languages.CompletionItemKind.Keyword, insertText: 'TO ' },

        // Operators and special characters
        // @ts-expect-error - Monaco typings are not up to date
        { label: '+', kind: monaco.languages.CompletionItemKind.Operator, insertText: '+' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '-', kind: monaco.languages.CompletionItemKind.Operator, insertText: '-' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '^', kind: monaco.languages.CompletionItemKind.Operator, insertText: '^' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '~', kind: monaco.languages.CompletionItemKind.Operator, insertText: '~' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '*', kind: monaco.languages.CompletionItemKind.Operator, insertText: '*' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '?', kind: monaco.languages.CompletionItemKind.Operator, insertText: '?' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '[', kind: monaco.languages.CompletionItemKind.Bracket, insertText: '[' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: ']', kind: monaco.languages.CompletionItemKind.Bracket, insertText: ']' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '{', kind: monaco.languages.CompletionItemKind.Bracket, insertText: '{' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '}', kind: monaco.languages.CompletionItemKind.Bracket, insertText: '}' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '(', kind: monaco.languages.CompletionItemKind.Bracket, insertText: '(' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: ')', kind: monaco.languages.CompletionItemKind.Bracket, insertText: ')' },
        // @ts-expect-error - Monaco typings are not up to date
        { label: '"', kind: monaco.languages.CompletionItemKind.Operator, insertText: '"' },
      ];

      return { suggestions };
    }
  });

  // Define the theme
  // @ts-expect-error - Monaco typings are not up to date
  monaco.editor.defineTheme('luceneTheme', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'identifier', foreground: '0000FF', fontStyle: 'bold' },
      { token: 'keyword', foreground: '008800', fontStyle: 'bold' },
      { token: 'string', foreground: 'DD0000' },
      { token: 'number', foreground: '098658' },
      { token: 'date', foreground: '811F3F', fontStyle: 'italic' },
      { token: 'operator', foreground: '000000', fontStyle: 'bold' },
      { token: 'delimiter', foreground: '000000' },
      { token: 'wildcard', foreground: 'FF0000', fontStyle: 'bold' }
    ],
    colors: {
      'editor.foreground': '#000000',
      'editor.background': '#FFFFFF',
      'editor.selectionBackground': '#ADD6FF',
      'editor.lineHighlightBackground': '#F0F0F0',
      'editorCursor.foreground': '#000000',
      'editorWhitespace.foreground': '#BFBFBF'
    }
  });
}

const monacoConfig: NgxMonacoEditorConfig = {
  baseUrl: 'assets',
  defaultOptions: { scrollBeyondLastLine: false },
  onMonacoLoad
};


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideHttpClient(withInterceptors([
      authInterceptor,
      apiErrorInterceptor,
      acceptLanguageInterceptor
    ])),
    importProvidersFrom(MonacoEditorModule.forRoot(monacoConfig)),
    provideRouter(appRoutes, withRouterConfig({
      onSameUrlNavigation: 'reload',
    })),
    provideTranslateService({
      defaultLanguage: 'en'
    }),
    ClearingTicketCustomService,
    {
      provide: LOCALE_ID,
      useFactory: (translateService: TranslateService) => translateService.currentLang || 'en',
      deps: [TranslateService]
    },
    MessageService,
    ...provideAuthentication(environment.KEYCLOAK),
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'Europe/Berlin' } },
    { provide: ITU_CODE, useValue: Array.isArray(environment.ITU_CODE) ? environment.ITU_CODE : [environment.ITU_CODE] },
    { provide: OWNER_SUBENTITIES, useValue: environment.OWNER_SUB_ENTITIES},
    { provide: CLEARING_API_URI, useValue: environment.CLEARING_API_ENDPOINT },
    { provide: CLEARING_CUSTOM_API_URI, useValue: environment.CLEARING_CUSTOM_API_ENDPOINT },
    { provide: LIGHT_PARTNER_API_PATH, useValue: environment.LIGHT_PARTNER_API_PATH },
    { provide: SEARCH_ENDPOINT, useValue: environment.SEARCH_ENDPOINT }
  ]
};
